import React from "react";
import { LoaderCircle, X } from "lucide-react";
import { formatQuantityTons } from "../hooks/useDataManagerOrdersUtils.js";
import { buttonToneClasses, joinClasses } from "./resultsPrimitives/shared.js";

const SAVE_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3.5 text-sm font-semibold shadow-none transition hover:shadow-none disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.optimizer,
]);

const TONNAGE_INPUT_CLASS_NAME = joinClasses([
  "h-8 w-full min-w-0 rounded-[10px] border border-slate-200 bg-white px-2.5 text-left text-sm font-semibold tabular-nums text-slate-800 outline-hidden",
  "focus:border-emerald-500 disabled:bg-slate-100 disabled:text-slate-500",
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
]);

function toInputValue(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? String(parsedValue) : "";
}

function toDraftTonnage(actualValue) {
  return toInputValue(actualValue);
}

function PlannedTonnageHint({ value }) {
  const parsedValue = Number(value);
  const displayValue = value === null || value === undefined || value === "" || !Number.isFinite(parsedValue)
    ? "—"
    : `${formatQuantityTons(parsedValue)} t`;
  return <span className="text-[10px] font-medium normal-case tracking-normal text-slate-500">Prévu : {displayValue}</span>;
}

function parseTonnage(value, label) {
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "" || normalizedValue === null || normalizedValue === undefined) {
    return null;
  }
  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    throw new Error(`${label} doit être un nombre positif ou zéro.`);
  }
  return parsedValue;
}

function normalizeStatus(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeOptionalText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function isDeliveredApprovalContext(order) {
  const approvalStatus = normalizeStatus(order?.billing_approval_status);
  return normalizeStatus(order?.dispatch_status) === "completed"
    || approvalStatus === "pending"
    || approvalStatus === "approved";
}

export function buildOrderExecutionPayload({
  correctionReason,
  deliveryDrafts,
  loadedDraft,
  markDelivered,
  order,
}) {
  if (!order?.updated_at) {
    throw new Error("La version de la commande est absente. Rafraîchissez le Data Manager.");
  }

  const payload = {
    expected_updated_at: order.updated_at,
    mark_delivered: Boolean(markDelivered),
  };
  const currentLoadedQuantity = parseTonnage(order.actual_loaded_quantity_tons, "Chargé réel T");
  const nextLoadedQuantity = parseTonnage(loadedDraft, "Chargé réel T");
  let changesExistingTonnage = false;
  if (markDelivered && nextLoadedQuantity === null) {
    throw new Error("Chargé réel T est requis avant de marquer la commande Livrée.");
  }
  if (!Object.is(currentLoadedQuantity, nextLoadedQuantity)) {
    payload.actual_loaded_quantity_tons = nextLoadedQuantity;
    changesExistingTonnage = currentLoadedQuantity !== null;
  }

  const currentDeliveriesById = new Map(
    (Array.isArray(order.deliveries) ? order.deliveries : [])
      .filter((delivery) => delivery?.delivery_id)
      .map((delivery) => [String(delivery.delivery_id), delivery]),
  );
  if (markDelivered && deliveryDrafts.length === 0) {
    throw new Error("Au moins une livraison canonique est requise pour marquer la commande Livrée.");
  }
  const changedDeliveries = [];
  deliveryDrafts.forEach((deliveryDraft, index) => {
    const deliveryId = String(deliveryDraft.delivery_id || "").trim();
    if (!deliveryId) {
      throw new Error(`L'identifiant de la livraison ${index + 1} est absent.`);
    }
    const currentDelivery = currentDeliveriesById.get(deliveryId);
    const currentQuantity = parseTonnage(
      currentDelivery?.actual_delivered_quantity_tons,
      `Livré réel T · ${deliveryId}`,
    );
    const nextQuantity = parseTonnage(deliveryDraft.value, `Livré réel T · ${deliveryId}`);
    if (markDelivered && nextQuantity === null) {
      throw new Error(`Livré réel T · ${deliveryId} est requis avant de marquer la commande Livrée.`);
    }
    const quantityChanged = !Object.is(currentQuantity, nextQuantity);
    if (!quantityChanged) {
      return;
    }
    if (!currentDelivery?.updated_at) {
      throw new Error(`La version de la livraison ${deliveryId} est absente. Rafraîchissez le Data Manager.`);
    }
    const deliveryUpdate = {
      delivery_id: deliveryId,
      expected_updated_at: currentDelivery.updated_at,
    };
    if (quantityChanged) {
      deliveryUpdate.actual_delivered_quantity_tons = nextQuantity;
      changesExistingTonnage = changesExistingTonnage || currentQuantity !== null;
    }
    changedDeliveries.push(deliveryUpdate);
  });
  if (changedDeliveries.length > 0) {
    payload.deliveries = changedDeliveries;
  }

  if (changesExistingTonnage && isDeliveredApprovalContext(order)) {
    const normalizedCorrectionReason = normalizeOptionalText(correctionReason);
    if (!normalizedCorrectionReason) {
      throw new Error("Le motif de correction est obligatoire lorsqu’un tonnage déjà saisi est modifié.");
    }
    payload.correction_reason = normalizedCorrectionReason;
  }

  if (!("actual_loaded_quantity_tons" in payload) && !("deliveries" in payload) && !payload.mark_delivered) {
    throw new Error("Aucune modification à enregistrer.");
  }
  return payload;
}

function normalizeTonnageForComparison(value) {
  if (value === null || value === undefined || (typeof value === "string" && !value.trim())) {
    return null;
  }
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : value;
}

export function orderExecutionRequiresCorrectionReason({ deliveryDrafts = [], loadedDraft = "", order = null } = {}) {
  if (!isDeliveredApprovalContext(order)) {
    return false;
  }

  const currentLoadedQuantity = normalizeTonnageForComparison(order?.actual_loaded_quantity_tons);
  const nextLoadedQuantity = normalizeTonnageForComparison(loadedDraft);
  if (currentLoadedQuantity !== null && !Object.is(currentLoadedQuantity, nextLoadedQuantity)) {
    return true;
  }

  const currentDeliveriesById = new Map(
    (Array.isArray(order?.deliveries) ? order.deliveries : [])
      .filter((delivery) => delivery?.delivery_id)
      .map((delivery) => [String(delivery.delivery_id), delivery]),
  );
  return (Array.isArray(deliveryDrafts) ? deliveryDrafts : []).some((deliveryDraft) => {
    const currentDelivery = currentDeliveriesById.get(String(deliveryDraft?.delivery_id || ""));
    const currentQuantity = normalizeTonnageForComparison(currentDelivery?.actual_delivered_quantity_tons);
    const nextQuantity = normalizeTonnageForComparison(deliveryDraft?.value);
    return currentQuantity !== null && !Object.is(currentQuantity, nextQuantity);
  });
}

export default function DataManagerOrderExecutionDialog({
  canMarkDelivered = false,
  canWriteActualTonnage = false,
  errorMessage = "",
  isPending = false,
  onClose,
  onSubmit,
  order,
}) {
  const [loadedDraft, setLoadedDraft] = React.useState(() => (
    toDraftTonnage(order?.actual_loaded_quantity_tons)
  ));
  const [deliveryDrafts, setDeliveryDrafts] = React.useState(() => (
    (Array.isArray(order?.deliveries) ? order.deliveries : []).map((delivery) => ({
      delivery_id: delivery?.delivery_id || "",
      label: delivery?.client_name || delivery?.delivery_id || "Livraison",
      plannedQuantity: delivery?.quantity_tons ?? null,
      value: toDraftTonnage(delivery?.actual_delivered_quantity_tons),
    }))
  ));
  const isAlreadyDelivered = normalizeStatus(order?.dispatch_status) === "completed";
  const [correctionReason, setCorrectionReason] = React.useState("");
  const [validationMessage, setValidationMessage] = React.useState("");
  const isReadOnly = !canWriteActualTonnage && !canMarkDelivered;
  const requiresCorrectionReason = orderExecutionRequiresCorrectionReason({ deliveryDrafts, loadedDraft, order });
  const canShowMarkDeliveredAction = canMarkDelivered && !isAlreadyDelivered && deliveryDrafts.length > 0;
  const saveDisabled = isPending || (requiresCorrectionReason && !correctionReason.trim());

  function handleDeliveryDraftChange(deliveryId, value) {
    setDeliveryDrafts((currentDrafts) => currentDrafts.map((deliveryDraft) => (
      deliveryDraft.delivery_id === deliveryId ? { ...deliveryDraft, value } : deliveryDraft
    )));
  }

  function submitExecution({ markAsDelivered = false } = {}) {
    setValidationMessage("");
    try {
      const payload = buildOrderExecutionPayload({
        correctionReason,
        deliveryDrafts,
        loadedDraft,
        markDelivered: Boolean(markAsDelivered) && canShowMarkDeliveredAction,
        order,
      });
      onSubmit?.(payload);
    } catch (error) {
      setValidationMessage(error instanceof Error ? error.message : "Les tonnages saisis sont invalides.");
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    submitExecution({ markAsDelivered: false });
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/28 px-4 py-6 backdrop-blur-xs" role="presentation">
      <form
        role="dialog" aria-modal="true"
        aria-label={`Tonnages réels de la commande ${order?.order_id || ""}`.trim()}
        className="grid min-w-0 max-h-[calc(100dvh-3rem)] w-full max-w-xl gap-4 overflow-x-hidden overflow-y-auto rounded-overlay border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.22)]"
        onSubmit={handleSubmit}
      >
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="grid min-w-0 gap-1">
            <p className="m-0 text-[10px] font-bold uppercase tracking-[0.16em] text-accent">
              {isReadOnly ? "Consultation des tonnages réels" : "Saisie des tonnages réels"}
            </p>
            <h3 className="m-0 text-base font-semibold text-accent">Commande {order?.order_id || "—"}</h3>
            <p className="m-0 text-xs leading-5 text-slate-500">
              {isReadOnly
                ? "Les données réelles sont affichées en lecture seule."
                : "Ces valeurs alimentent le Portail, le Dispatch et la Facturation."}
            </p>
          </div>
          <button
            type="button"
            aria-label="Fermer"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:text-slate-950 disabled:opacity-50"
            disabled={isPending}
            onClick={onClose}
          >
            <X size={15} />
          </button>
        </div>

        <div className="grid gap-3">
          {deliveryDrafts.length <= 1 ? (
            <div className="grid gap-3">
              {deliveryDrafts[0]?.label ? (
                <p className="m-0 min-w-0 truncate text-xs font-semibold text-slate-700" title={deliveryDrafts[0].label}>
                  {deliveryDrafts[0].label}
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <label className="grid min-w-0 gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Chargé réel T</span>
                  <input
                    autoFocus={canWriteActualTonnage}
                    className={TONNAGE_INPUT_CLASS_NAME}
                    disabled={!canWriteActualTonnage || isPending}
                    min="0"
                    onChange={(event) => setLoadedDraft(event.target.value)}
                    placeholder="0"
                    step="0.01"
                    type="number"
                    value={loadedDraft}
                  />
                  <PlannedTonnageHint value={order?.quantity_tons} />
                </label>
                {deliveryDrafts[0] ? (
                  <label className="grid min-w-0 gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    Livré réel T
                    <input
                      aria-label={`Livré réel T · ${deliveryDrafts[0].label}`}
                      className={TONNAGE_INPUT_CLASS_NAME}
                      disabled={!canWriteActualTonnage || isPending}
                      min="0"
                      onChange={(event) => handleDeliveryDraftChange(deliveryDrafts[0].delivery_id, event.target.value)}
                      placeholder="0"
                      step="0.01"
                      type="number"
                      value={deliveryDrafts[0].value}
                    />
                    <PlannedTonnageHint value={deliveryDrafts[0].plannedQuantity} />
                  </label>
                ) : (
                  <div />
                )}
              </div>
            </div>
          ) : (
            <>
              <label className="grid gap-1 sm:max-w-[12rem]">
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Chargé réel T</span>
                <input
                  autoFocus={canWriteActualTonnage}
                  className={TONNAGE_INPUT_CLASS_NAME}
                  disabled={!canWriteActualTonnage || isPending}
                  min="0"
                  onChange={(event) => setLoadedDraft(event.target.value)}
                  placeholder="0"
                  step="0.01"
                  type="number"
                  value={loadedDraft}
                />
                <PlannedTonnageHint value={order?.quantity_tons} />
              </label>
              <div className="grid gap-3">
                <p className="m-0 text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Livraisons</p>
                {deliveryDrafts.map((deliveryDraft, index) => (
                  <div key={deliveryDraft.delivery_id || index} className="grid gap-2">
                    <p className="m-0 min-w-0 truncate text-xs font-semibold text-slate-700" title={deliveryDraft.label}>
                      {deliveryDraft.label}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="grid min-w-0 gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                        Livré réel T
                        <input
                          aria-label={`Livré réel T · ${deliveryDraft.label}`}
                          className={TONNAGE_INPUT_CLASS_NAME}
                          disabled={!canWriteActualTonnage || isPending}
                          min="0"
                          onChange={(event) => handleDeliveryDraftChange(deliveryDraft.delivery_id, event.target.value)}
                          placeholder="0"
                          step="0.01"
                          type="number"
                          value={deliveryDraft.value}
                        />
                        <PlannedTonnageHint value={deliveryDraft.plannedQuantity} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {deliveryDrafts.length === 0 ? (
            <p className="m-0 text-xs text-amber-700">Aucune livraison canonique n’est disponible.</p>
          ) : null}
        </div>

        {!isReadOnly && requiresCorrectionReason ? (
          <label className="grid gap-1.5 text-xs font-semibold text-slate-700">
            Motif de correction
            <textarea
              autoFocus={!canWriteActualTonnage}
              className="min-h-20 resize-y rounded-[10px] border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-hidden focus:border-emerald-500 disabled:bg-slate-100 disabled:text-slate-500"
              disabled={isPending}
              onChange={(event) => setCorrectionReason(event.target.value)}
              maxLength={1000}
              placeholder="Expliquez pourquoi le tonnage réel est corrigé"
              required
              value={correctionReason}
            />
            <span className="font-normal text-slate-500">Ce motif sera conservé dans la traçabilité.</span>
          </label>
        ) : null}

        {isReadOnly ? (
          <p className="m-0 rounded-[12px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Mode lecture seule : vos permissions ne permettent pas de modifier l’exécution.
          </p>
        ) : !isAlreadyDelivered && !canMarkDelivered ? (
          <p className="m-0 rounded-[12px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Vous pouvez modifier les tonnages, mais le droit « Marquer une commande livrée » n’est pas attribué.
          </p>
        ) : null}

        {validationMessage || errorMessage ? (
          <p className="m-0 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold leading-5 text-rose-700" role="alert">
            {validationMessage || errorMessage}
          </p>
        ) : null}

        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <button
            type="button"
            className="inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-slate-300 disabled:opacity-60"
            disabled={isPending}
            onClick={onClose}
          >
            Fermer
          </button>
          {!isReadOnly ? (
            <>
              <button
                type="submit"
                className={canShowMarkDeliveredAction
                  ? "inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-700 shadow-none transition hover:border-slate-300 hover:shadow-none disabled:cursor-not-allowed disabled:opacity-60"
                  : SAVE_BUTTON_CLASS_NAME}
                disabled={saveDisabled}
              >
                {isPending ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {isPending ? "Enregistrement..." : "Enregistrer"}
              </button>
              {canShowMarkDeliveredAction ? (
                <button
                  type="button"
                  className={SAVE_BUTTON_CLASS_NAME}
                  disabled={saveDisabled}
                  onClick={() => submitExecution({ markAsDelivered: true })}
                  title="Enregistre les tonnages, marque la commande Livrée et la place dans « À approuver »"
                >
                  {isPending ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                  {isPending ? "Enregistrement..." : "Enregistrer et marquer livrée"}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
}
